import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';

import { useRevenueByProduct } from '../../hooks/use-dashboard';

const brl = (v: number): string =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

// Hex resolvidos pra Recharts (não aceita CSS var). Match design tokens.
const BRAND = '#7c3aed'; // var(--brand)
const MUTED = '#a1a1aa'; // var(--muted)

/** Story 6.4 — Horizontal bar chart top 10 SaaS por MRR. */
export function RevenueByProductChart() {
  const query = useRevenueByProduct(10);

  if (query.isLoading) {
    return <Skeleton />;
  }

  if (query.isError) {
    return (
      <Card title="Receita por produto">
        <div className="p-4 text-sm text-bad">Erro ao carregar dados.</div>
      </Card>
    );
  }

  const items = query.data?.data.items ?? [];

  if (items.length === 0) {
    return (
      <Card title="Receita por produto">
        <div className="py-8 text-center text-sm text-muted">
          Sem dados de MRR por produto (aguarda snapshot — cron 00:30 BRT).
        </div>
      </Card>
    );
  }

  const data = items.map((it) => ({
    name: it.product_name,
    mrr: it.mrr,
    pct: it.pct,
  }));

  return (
    <Card title="Receita por produto (MRR)">
      <div className="h-72 w-full p-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={data}
            margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
          >
            <XAxis
              type="number"
              stroke={MUTED}
              fontSize={11}
              tickFormatter={(v) => brl(Number(v))}
            />
            <YAxis dataKey="name" type="category" stroke={MUTED} fontSize={12} width={120} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(124, 58, 237, 0.08)' }} />
            <Bar dataKey="mrr" fill={BRAND} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0]?.payload as { name: string; mrr: number; pct: number };
  return (
    <div className="rounded-md border border-line bg-panel px-3 py-2 text-xs shadow-md">
      <div className="font-medium text-ink">{item.name}</div>
      <div className="mt-1 font-mono tabular-nums text-ink-2">{brl(item.mrr)}</div>
      <div className="text-muted">{item.pct.toFixed(1)}% do total</div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-panel">
      <div className="border-b border-line px-4 py-3">
        <h3 className="text-sm font-medium text-ink">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Skeleton() {
  return <div className="h-80 animate-pulse rounded-lg border border-line bg-panel-2" />;
}
